/* eslint-disable unicorn/no-null */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import PublicationDropdown from '../../../shared/publication-dropdown';
import CountryDropdown from '../../../shared/country-dropdown';
import RegionDropdown from '../../../shared/region-dropdown';
import DestinationDropdown from '../../../shared/destination-dropdown';
import AccommodationDropdown from '../../../shared/accommodation-dropdown';
import TransportHubDropdown from '../../../shared/transport-hub-dropdown';
import {
  getFilteredRegions,
  getFilteredDestinations,
  getFilteredAccommodations,
} from '../../../shared/geography-filter';
import {
  getGeography,
  getAccommodations,
  postAccommodationBlocking,
  getAccommodationBlockingById,
  getAccommodationBlockings,
  GENERIC_FAILURE_MESSAGE,
} from '../../../../store/actions';
import {
  selectGeographies,
  selectAccommodations,
  selectAccessToken,
  selectPublications,
  selectTransportHubs,
} from '../../../../store/selectors';
// eslint-disable-next-line max-len
import AccommodationBlockingDateRange from '../../../shared/accommodation-blocking-date-range/accommodation-blocking-date-range';
import AccommodationBlockingType from '../../../shared/accommodation-blocking-type/accommodation-blocking-type';
// eslint-disable-next-line max-len
import AccommodationBlockingDirection from '../../../shared/accommodation-blocking-direction/accommodation-blocking-direction';
// eslint-disable-next-line max-len
import AccommodationBlockingViolation from '../../../shared/accommodation-blocking-violation/accommodation-blocking-violation';
import {
  IAccommodationBlockingForm,
  MAX_DURATIONS_LENGTH,
  MAX_REMARK_LENGTH,
  getDefaultAccommodationBlockingForm,
} from '../../../shared/accommodation-blocking-form/accommodation-blocking-form';
// eslint-disable-next-line max-len
import { validateAccommodationBlocking } from '../../../shared/accommodation-blocking-form/accommodation-blocking-validation';
import {
  buildAccommodationBlockingPayload,
  mapAccommodationBlockingToForm,
  IExistingAccommodationBlocking,
  // eslint-disable-next-line max-len
} from '../../../shared/accommodation-blocking-form/accommodation-blocking-form-mapper';
import styles from './accommodation-blocking-form-page.module.scss';

const LOAD_FAILURE_MESSAGE =
  'This Accommodation Blocking could not be loaded. It may have been deleted, or something went wrong.';

export type AccommodationBlockingFormMode = 'new' | 'edit' | 'copy';

interface IAccommodationBlockingFormPageProperties {
  mode?: AccommodationBlockingFormMode;
}

function AccommodationBlockingFormPage({ mode = 'new' }: IAccommodationBlockingFormPageProperties) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { accounts } = useMsal();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = mode === 'edit';
  // copy, like edit, prefills the form from a fetched source record (see ADR-0012) - only
  // edit keeps a reference to it (via setExisting below), so copy's Save always creates a
  // brand-new record instead of updating the source.
  const needsSourceRecord = mode !== 'new';

  const geographies = useSelector(selectGeographies);
  const accommodationList = useSelector(selectAccommodations);
  const publicationList = useSelector(selectPublications);
  const transportHubList = useSelector(selectTransportHubs);
  const accessToken = useSelector(selectAccessToken);

  const [form, setForm] = useState<IAccommodationBlockingForm>(getDefaultAccommodationBlockingForm());
  const [violations, setViolations] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [existing, setExisting] = useState<IExistingAccommodationBlocking | undefined>();
  const [isLoading, setIsLoading] = useState(needsSourceRecord);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!needsSourceRecord) {
      return;
    }

    // On a fresh full-page load of /accommodation-blocking/edit/:id (or copy/:id), this effect
    // can run before the access token has been populated in the store (it's set asynchronously
    // after MSAL authentication resolves, see app.tsx). Wait for it, the same way
    // getGeography/getAccommodations below do, instead of firing the fetch with no token and
    // failing.
    if (!accessToken) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadFailed(false);

    (async () => {
      const result = await dispatch(getAccommodationBlockingById(Number(id)));
      if (cancelled) {
        return;
      }

      if (result.status === 'found') {
        setForm(mapAccommodationBlockingToForm(result.blocking));
        // Only edit keeps the source's identity/audit fields around for Save to reuse. Copy
        // deliberately leaves `existing` unset so buildAccommodationBlockingPayload (ADR-0009)
        // treats the save as a brand-new record - see ADR-0012.
        if (isEditMode) {
          setExisting({
            id: result.blocking.id,
            accommodationBlockingKey: result.blocking.accommodationBlockingKey,
            createdBy: result.blocking.createdBy,
            createdDate: result.blocking.createdDate,
          });
        }
      } else {
        setLoadFailed(true);
      }

      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, id, isEditMode, needsSourceRecord, accessToken]);

  const {
    publications,
    countries,
    regions,
    destinations,
    accommodations,
    airportsOrBusStops,
    dateRange,
    direction,
    blockingType,
    remark,
    durations,
  } = form;

  useEffect(() => {
    if (geographies.length === 0 && accessToken) {
      dispatch(getGeography());
    }
  }, [dispatch, accessToken]);

  useEffect(() => {
    if (accommodationList.length === 0 && accessToken) {
      dispatch(getAccommodations());
    }
  }, [dispatch, accessToken]);

  const filteredRegionCodes = useMemo(() => getFilteredRegions(geographies, countries), [geographies, countries]);
  const filteredDestinationCodes = useMemo(
    () => getFilteredDestinations(geographies, countries, regions),
    [geographies, countries, regions]
  );
  const filteredAccommodationCodes = useMemo(
    () => getFilteredAccommodations(accommodationList, countries, regions, destinations),
    [accommodationList, countries, regions, destinations]
  );

  const allowedRegionCodes = geographies.length === 0 ? undefined : filteredRegionCodes;
  const allowedDestinationCodes = geographies.length === 0 ? undefined : filteredDestinationCodes;
  const allowedAccommodationCodes = accommodationList.length === 0 ? undefined : filteredAccommodationCodes;

  const updateForm = (changes: Partial<IAccommodationBlockingForm>) => {
    setForm((previous) => ({ ...previous, ...changes }));
    setViolations([]);
  };

  const handleCountriesChange = (event: { value: string[] }) => {
    updateForm({ countries: event.value, regions: [], destinations: [], accommodations: [] });
  };

  const handleRegionsChange = (event: { value: string[] }) => {
    updateForm({ regions: event.value, destinations: [], accommodations: [] });
  };

  const handleDestinationsChange = (event: { value: string[] }) => {
    updateForm({ destinations: event.value, accommodations: [] });
  };

  const handleCancel = () => {
    navigate('/accommodation-blocking/overview');
  };

  const handleSave = async () => {
    setViolations([]);

    const formViolations = validateAccommodationBlocking(form);
    if (formViolations.length > 0) {
      setViolations(formViolations);
      return;
    }

    setIsSaving(true);
    try {
      const { payload, unresolved } = buildAccommodationBlockingPayload(
        form,
        { publications: publicationList, accommodations: accommodationList, transportHubs: transportHubList },
        accounts[0]?.username ?? '',
        existing
      );

      if (unresolved.length > 0) {
        setViolations(unresolved);
        return;
      }

      const result = await dispatch(postAccommodationBlocking(payload));

      if (result.status === 'saved') {
        // Overview only re-fetches when its list is empty (see accommodation-blocking-overview.tsx).
        // Dispatch a refresh (not awaited, so navigation isn't delayed) so Overview's redux-connected
        // rows update once it resolves, satisfying "Overview refreshes with the latest changes"
        // (TD-10655 step 10) for both New and Edit saves.
        dispatch(getAccommodationBlockings());
        navigate('/accommodation-blocking/overview');
        return;
      }

      if (result.status === 'rejected') {
        setViolations(result.messages);
        return;
      }

      setViolations([GENERIC_FAILURE_MESSAGE]);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.accommodationBlockingFormPage}>
        <div className={styles.loading}>
          <ProgressSpinner />
        </div>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className={styles.accommodationBlockingFormPage}>
        <div className={styles.error}>
          <p>{LOAD_FAILURE_MESSAGE}</p>
          <Button label="Back to Overview" onClick={() => navigate('/accommodation-blocking/overview')} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.accommodationBlockingFormPage}>
      <h1 className={styles.heading}>
        {isEditMode ? 'Edit Accommodation Blocking' : 'New Accommodation Blocking'}
        {isEditMode && existing && <span className={styles.recordId}> - Id: {existing.id}</span>}
      </h1>

      <div className={styles.field}>
        <label htmlFor="publications">Publications</label>
        <PublicationDropdown
          selectedValue={publications}
          onChange={(event) => updateForm({ publications: event.value })}
        />
      </div>

      <div className={styles.field}>
        <label htmlFor="countries">Countries</label>
        <CountryDropdown selectedValue={countries} onChange={handleCountriesChange} />
      </div>

      <div className={styles.field}>
        <label htmlFor="regions">Regions</label>
        <RegionDropdown selectedValue={regions} onChange={handleRegionsChange} allowedCodes={allowedRegionCodes} />
      </div>

      <div className={styles.field}>
        <label htmlFor="destinations">Destinations</label>
        <DestinationDropdown
          selectedValue={destinations}
          onChange={handleDestinationsChange}
          allowedCodes={allowedDestinationCodes}
        />
      </div>

      <div className={styles.field}>
        <label htmlFor="accommodations">Accommodations</label>
        <AccommodationDropdown
          selectedValue={accommodations}
          onChange={(event) => updateForm({ accommodations: event.value })}
          allowedCodes={allowedAccommodationCodes}
        />
      </div>

      <div className={styles.field}>
        <label htmlFor="airportsOrBusStops">Airports / Bus Stops</label>
        <TransportHubDropdown
          selectedValue={airportsOrBusStops}
          onChange={(event) => updateForm({ airportsOrBusStops: event.value })}
        />
      </div>

      <AccommodationBlockingDirection
        value={direction}
        visible={airportsOrBusStops.length > 0}
        onChange={(newDirection) => updateForm({ direction: newDirection })}
      />

      <div className={styles.field}>
        <AccommodationBlockingDateRange
          value={dateRange}
          onChange={(newDateRange) => updateForm({ dateRange: newDateRange })}
        />
      </div>

      <div className={styles.field}>
        <span className={styles.radioGroupLabel}>Type</span>
        <AccommodationBlockingType
          value={blockingType}
          onChange={(newBlockingType) => updateForm({ blockingType: newBlockingType })}
        />
      </div>

      <div className={styles.field}>
        <label htmlFor="durations">Durations</label>
        <InputText
          id="durations"
          value={durations}
          onChange={(event) => updateForm({ durations: event.target.value })}
          maxLength={MAX_DURATIONS_LENGTH}
          placeholder="e.g. 1,3-5"
        />
      </div>

      <div className={styles.field}>
        <label htmlFor="remark">Remark</label>
        <InputTextarea
          id="remark"
          value={remark}
          onChange={(event) => updateForm({ remark: event.target.value })}
          maxLength={MAX_REMARK_LENGTH}
          rows={3}
        />
      </div>

      <AccommodationBlockingViolation violations={violations} />

      <div className={styles.actions}>
        <Button label="Save" onClick={handleSave} loading={isSaving} disabled={isSaving} />
        <Button label="Cancel" outlined onClick={handleCancel} disabled={isSaving} />
      </div>
    </div>
  );
}

export default AccommodationBlockingFormPage;
